import React, {useEffect, useState} from "react";
import './../css/blog.scss';
import BlogData from './../data/blog.json';
import { Link } from 'react-router-dom';
import SEO from './../components/SEO/SEO';
import { seoConfig } from './../config/seoConfig';
import useAnalyticsEventTracker from './../helpers/useAnalyticsEventTracker';
import useAnalyticsScrollEvent from './../helpers/useAnalyticsScrollEvent';

function Blog() {
  const gaEventTracker = useAnalyticsEventTracker('Blog');
  useAnalyticsScrollEvent('Blog');
  let {featured, list} = BlogData.blog;

  // State for filtering, sorting, and pagination
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [sortOrder, setSortOrder] = useState('newest'); // 'newest' or 'oldest'
  const [currentPage, setCurrentPage] = useState(1);
  const articlesPerPage = 6;

  // Get all unique categories
  const allCategories = ['All'];
  list.forEach(article => {
    if (article.categories) {
      article.categories.forEach(cat => {
        if (!allCategories.includes(cat)) {
          allCategories.push(cat);
        }
      });
    }
  });

  // Filter articles by category
  let filteredArticles = selectedCategory === 'All'
    ? list
    : list.filter(article => article.categories && article.categories.includes(selectedCategory));

  // Sort articles by date
  filteredArticles = [...filteredArticles].sort((a, b) => {
    const dateA = new Date(a.datePublished || a.date);
    const dateB = new Date(b.datePublished || b.date);
    return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
  });

  // Pagination logic
  const indexOfLastArticle = currentPage * articlesPerPage;
  const indexOfFirstArticle = indexOfLastArticle - articlesPerPage;
  const currentArticles = filteredArticles.slice(indexOfFirstArticle, indexOfLastArticle);
  const totalPages = Math.ceil(filteredArticles.length / articlesPerPage);

  // Reset to page 1 when category changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory]);

  // Handle category click
  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    gaEventTracker(`Category Filter - ${category}`, 'Filter');
  };

  // Handle sort change
  const handleSortChange = (e) => {
    setSortOrder(e.target.value);
    setCurrentPage(1);
    gaEventTracker(`Sort - ${e.target.value}`, 'Sort');
  };

  // Handle pagination
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    gaEventTracker(`Page ${pageNumber}`, 'Pagination');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Blog list schema for SEO
  const blogListSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Meetworkplace Employee Retention Blog",
    "description": "Expert insights on employee retention, workplace culture, and engagement strategies",
    "url": "https://tomeetworkplace.com/blog",
    "publisher": {
      "@type": "Organization",
      "name": "Meetworkplace",
      "logo": {
        "@type": "ImageObject",
        "url": "https://tomeetworkplace.com/logo512.png"
      }
    },
    "blogPost": list.map(article => ({
      "@type": "BlogPosting",
      "headline": article.headline,
      "description": article.excerpt || article.description,
      "url": `https://tomeetworkplace.com/blog/${article.slug}`,
      "datePublished": article.datePublished,
      "dateModified": article.dateModified,
      "author": {
        "@type": "Organization",
        "name": article.author || "Meetworkplace Team"
      }
    }))
  };

  return (
    <div className="blog-container">
      <SEO
        title={seoConfig.blog.title}
        description={seoConfig.blog.description}
        keywords={seoConfig.blog.keywords}
        structuredData={blogListSchema}
      />

      <section className="white featured">
        <Link to={`/blog/${featured.slug}`} onClick={()=> {gaEventTracker('Employee Retention','CTA Link')}} className="container row">
        <div className="col-xs-12 col-sm-5 main-image">
          <img src={featured.image.startsWith('http') ? featured.image : require(`./../assets/${featured.image}.jpg`)} alt={featured.headline}/>
        </div>
          <div className="col-xs-12 col-sm-7">
          <div className="intro-container">
              <div className="pill-container">
                {featured.categories && featured.categories.map((category, idx) => (
                  <span key={idx} className="pill">{category}</span>
                ))}
              </div>
              <div className="title-container">
                <h1>{featured.headline}</h1>
                <p>{featured.description}</p>
              </div>
            </div>
          </div>
        </Link> 
      </section>

      {/* Category Filter */}
      <section className="white category-filter-section">
        <div className="container">
          <div className="category-filter">
            {allCategories.map((category, idx) => (
              <button
                key={idx}
                className={`category-pill ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => handleCategoryClick(category)}
              >
                {category}
              </button>
            ))}
          </div>
          <div className="results-count">
            <span>Showing {currentArticles.length} of {filteredArticles.length} articles</span>
            <select
              className="sort-dropdown"
              value={sortOrder}
              onChange={handleSortChange}
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
            </select>
          </div>
        </div>
      </section>

      <section className="white no-top-margin">
        {currentArticles.map((item, i) => {
            return (
              <Link key={i} className="article-preview container row middle-xs"
                onClick={()=> {gaEventTracker('Blog Article | '+item.headline, 'CTA Link')}}
                to={`/blog/${item.slug}`}
              >

                  <div className="col-xs-12 col-sm-4 main-image">
                  <img src={item.image.startsWith('http') ? item.image : require(`./../assets/${item.image}.jpg`)} alt={item.headline}/>
                </div>
                  <div className="col-xs-12 col-sm-8">
                  <div className="intro-container">
                      <div className="pill-container">
                        {item.categories && item.categories.map((category, idx) => (
                          <span key={idx} className="pill">{category}</span>
                        ))}
                      </div>
                      <div className="title-container">
                        <h2>{item.headline}</h2>
                        <p>{item.description}</p>
                      </div>

                      <div className="author-date">{item.date}</div>

                    </div>
                  </div>

              </Link>
            )
          })
        }
      </section>

      {/* Pagination */}
      {totalPages > 1 && (
        <section className="white pagination-section">
          <div className="container">
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>

              {[...Array(totalPages)].map((_, index) => (
                <button
                  key={index}
                  className={`pagination-btn page-number ${currentPage === index + 1 ? 'active' : ''}`}
                  onClick={() => handlePageChange(index + 1)}
                >
                  {index + 1}
                </button>
              ))}

              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </section>
      )}

    </div>
  )
}

export default Blog;